import React from 'react';
import { ButtonNew, FlexBox, message, Typography } from '@helloworld1812/ws-components';
import { User, useUsersMeCached } from '@helloworld1812/hr-api';
import { IconBell } from '@helloworld1812/ws-icons';
import { useTranslation } from 'react-i18next';

import { HiringPaywallWrapper, useIsLiteTier } from '@/components/tier-lite';
import { NotificationConfig, useHrMutation, useHrQuery } from '@/api';

import { NotificationConfigsEditTable } from './notification-configs-edit-table';

const getFullPhoneNumber = (user: User) => {
  const { phone: phoneNumber, region_number: regionNumber } = user;
  if (!phoneNumber || !regionNumber) return undefined;
  return `+${regionNumber}${phoneNumber}`;
};

export function NotificationConfigsSetupStep() {
  const { user } = useUsersMeCached();
  const isLiteTier = useIsLiteTier();
  const { t } = useTranslation('hr-account-routes');

  const { data, isFetching, refetch } = useHrQuery({
    url: '/v1/notification_configs',
  });

  const { mutate: updateAllNotificationConfigs } = useHrMutation({
    url: '/v1/notification_configs/update_all',
    method: 'put',
  });

  const notificationSettings = (data?.notification_configs ?? []) as NotificationConfig[];

  const phone = getFullPhoneNumber(user);

  const isAllNotificationsOn = notificationSettings.every(({ settings }) => {
    return !!settings?.email && (!phone || !!settings?.sms);
  });

  const handleClickToggleAll = async () => {
    try {
      await updateAllNotificationConfigs({
        data: {
          notification_configs: notificationSettings.map((item) => ({
            ...item,
            settings: {
              ...item.settings,
              ...(phone && { sms: isLiteTier ? false : !isAllNotificationsOn }),
              email: !isAllNotificationsOn,
            },
          })),
        },
      });

      const timerId = setInterval(() => {
        refetch().then((res) => {
          if (isAllNotificationsOn) {
            const isAllOff = res.data?.data?.notification_configs?.every(({ settings }) => {
              return !settings?.email && (!phone || !settings?.sms);
            });
            if (isAllOff) {
              clearInterval(timerId);
              message.success(t('Notification settings updated successfully'));
            }
          } else {
            const isAllOn = res.data?.data?.notification_configs?.every(({ settings }) => {
              return !!settings?.email && (!phone || !!settings?.sms);
            });
            if (isAllOn) {
              clearInterval(timerId);
              message.success(t('Notification settings updated successfully'));
            }
          }
        });
      }, 1000);
      return () => clearInterval(timerId);
    } catch (error) {
      message.error(t('Failed to update notification settings'));
    }
  };

  return (
    <FlexBox column>
      <FlexBox justify="center">
        <Typography variant="h1" fontWeight="bold">
          {t('Notification setup')}
        </Typography>
      </FlexBox>
      <HiringPaywallWrapper block hasNoFunctionModule="messaging" mode="hover" disabledStyle="opacity">
        <ButtonNew
          variant="secondary"
          mt={32}
          mb={16}
          bgColor="gray200"
          height={50}
          border={0}
          onClick={handleClickToggleAll}
          icon={<IconBell size={20} />}
        >
          {isAllNotificationsOn ? t('Turn all notification OFF') : t('Turn all notification ON')}
        </ButtonNew>
      </HiringPaywallWrapper>
      <Typography variant="body" fontWeight="bold" mb={2}>
        {t('Notify me when...')}
      </Typography>
      <NotificationConfigsEditTable
        dataSource={notificationSettings}
        onUpdateSuccess={refetch}
        isFetching={isFetching}
        tableHeight="calc(100vh - 360px)"
      />
    </FlexBox>
  );
}
